/**
 * Chunk B1–B4 — Soft void on GR (PO-level: every Order line stamped).
 * Default GR list / health exclude voided. Voided filter + 7-day undo in B4.
 *
 * Permissions: anyone who can open Goods Receipting may void/delete/undo;
 * no extra role gate beyond GR tab access.
 *
 * B3 matrix:
 * - Not receipted / receipted, no invoice → allow
 * - Invoice attached only → allow + auto-unlink
 * - Reconciled / Sent to Xero → block
 * - Sent to supplier / CN Requested → block
 *
 * B4 undo: within 7 days if still not reconciled/Xero and not re-linked.
 */

import {
  getLinkedInvoicesForReceiptingOrder,
  isInCreditWorkflow,
  isOrderInvoiceReconciled,
} from './reconciliationHealth'
import { hasGoodsReceiptingInvoiceLinked } from './goodsReceiptingDeleteHelpers'

export const GR_VOID_REASONS = [
  'Entered in error',
  'Duplicate of another PO',
  'Cancelled by supplier',
  'Cancelled by venue',
  'Test / training',
  'Other',
]

export const GR_VOID_UNDO_WINDOW_DAYS = 7

export const GR_VOID_COPY = {
  actionTooltip: 'Cancel this order for records',
  actionLabel: 'Void',
  modalTitle: 'Void order',
  okText: 'Void order',
  reasonLabel: 'Reason',
  noteLabel: 'Note (optional)',
  consequences: 'Removed from Goods receipting and live spend. Kept for audit.',
  consequencesWithInvoice:
    'Removed from Goods receipting and live spend. Attached invoices are unlinked so they can be matched again. Kept for audit.',
  blockedAlready: 'This order is already voided.',
  blockedCredit: 'Finish or clear credit workflow on this order first.',
  blockedReconciled:
    'This order is reconciled / in Xero — reverse that before voiding.',
  undoLabel: 'Undo void',
  undoTooltip: 'Restore this order to Goods receipting',
  undoBlockedWindow:
    'Undo window has ended (7 days). Contact OMS support if you still need this restored.',
  undoBlockedFinance: 'This order is reconciled / in Xero — cannot undo void.',
  undoBlockedRelinked:
    'An invoice is linked to this PO again. Remove it before undoing void.',
  undoBlockedNotVoided: 'This order is not voided.',
}

export function isVoidedGoodsReceiptingOrder(order = {}) {
  return order?.isVoided === true || Boolean(order?.voidedAt)
}

/** Drop voided POs before health / default list rendering (defence in depth). */
export function excludeVoidedGoodsReceiptingOrders(orders = []) {
  return (orders || []).filter(order => !isVoidedGoodsReceiptingOrder(order))
}

export function onlyVoidedGoodsReceiptingOrders(orders = []) {
  return (orders || []).filter(order => isVoidedGoodsReceiptingOrder(order))
}

/**
 * Shape stamped onto each line of a voided PO.
 * voidedFromReceiptingStatus = PO receiptingStatus snapshot at void time.
 */
export function buildVoidedOrderLineFields({
  voidedBy = '',
  voidReason = '',
  voidNote = '',
  voidedFromReceiptingStatus = '',
  voidedAt = new Date(),
} = {}) {
  return {
    isVoided: true,
    voidedAt,
    voidedBy,
    voidReason,
    voidNote,
    voidedFromReceiptingStatus,
  }
}

/** Clears soft-void stamps when restoring a PO (Chunk B4). */
export function buildUndoVoidedOrderLineFields() {
  return {
    isVoided: false,
    voidedAt: null,
    voidedBy: '',
    voidReason: '',
    voidNote: '',
    voidedFromReceiptingStatus: '',
  }
}

export function isVoidBlockedByCreditWorkflow(order = {}) {
  if (isInCreditWorkflow(order)) return true
  return (order?.receiptingStatus || '') === 'CN Requested'
}

export function isVoidBlockedByFinance(order = {}, uploadedInvoices = []) {
  if (order?.sentToXero) return true
  return isOrderInvoiceReconciled(order, uploadedInvoices)
}

/** Attached / soft-linked invoices that should auto-unlink on void (not finance-closed). */
export function orderNeedsInvoiceUnlinkOnVoid(
  order = {},
  uploadedInvoices = [],
) {
  if (isVoidBlockedByFinance(order, uploadedInvoices)) return false
  if (hasGoodsReceiptingInvoiceLinked(order)) return true
  return getLinkedInvoicesForReceiptingOrder(order, uploadedInvoices).length > 0
}

export function collectAttachedInvoiceIdsForVoid(
  order = {},
  uploadedInvoices = [],
) {
  const ids = new Set()

  ;(Array.isArray(order?.attachedInvoices)
    ? order.attachedInvoices
    : []
  ).forEach(id => {
    if (id) ids.add(String(id))
  })
  ;(Array.isArray(order?.attachedInvoiceDetails)
    ? order.attachedInvoiceDetails
    : []
  ).forEach(detail => {
    const id = detail?._id || detail?.id
    if (id) ids.add(String(id))
  })

  getLinkedInvoicesForReceiptingOrder(order, uploadedInvoices).forEach(
    invoice => {
      const id = invoice?._id || invoice?.id
      if (id) ids.add(String(id))
    },
  )

  return [...ids]
}

export function getVoidConsequencesCopy(order = {}, uploadedInvoices = []) {
  if (orderNeedsInvoiceUnlinkOnVoid(order, uploadedInvoices)) {
    return GR_VOID_COPY.consequencesWithInvoice
  }
  return GR_VOID_COPY.consequences
}

/**
 * Void allowed for any PO on GR (manual + OMS) unless already voided,
 * in credit workflow, or invoice-reconciled / Xero.
 * Attached-only invoices allowed — auto-unlink on void (B3).
 */
export function canVoidGoodsReceiptingOrder(order = {}, uploadedInvoices = []) {
  if (isVoidedGoodsReceiptingOrder(order)) return false
  if (isVoidBlockedByCreditWorkflow(order)) return false
  if (isVoidBlockedByFinance(order, uploadedInvoices)) return false
  return true
}

export function getVoidGoodsReceiptingDisabledReason(
  order = {},
  uploadedInvoices = [],
) {
  if (isVoidedGoodsReceiptingOrder(order)) {
    return GR_VOID_COPY.blockedAlready
  }
  if (isVoidBlockedByCreditWorkflow(order)) {
    return GR_VOID_COPY.blockedCredit
  }
  if (isVoidBlockedByFinance(order, uploadedInvoices)) {
    return GR_VOID_COPY.blockedReconciled
  }
  return null
}

export function isValidVoidReason(reason) {
  return GR_VOID_REASONS.includes(String(reason || '').trim())
}

/**
 * B3 matrix classifier for tests / UI diagnostics.
 * @returns {'allow'|'allow_unlink'|'block_credit'|'block_finance'|'block_voided'}
 */
export function classifyVoidGoodsReceiptingGate(
  order = {},
  uploadedInvoices = [],
) {
  if (isVoidedGoodsReceiptingOrder(order)) return 'block_voided'
  if (isVoidBlockedByCreditWorkflow(order)) return 'block_credit'
  if (isVoidBlockedByFinance(order, uploadedInvoices)) return 'block_finance'
  if (orderNeedsInvoiceUnlinkOnVoid(order, uploadedInvoices)) {
    return 'allow_unlink'
  }
  return 'allow'
}

export function isWithinVoidUndoWindow(order = {}, now = new Date()) {
  if (!order?.voidedAt) return false
  const voidedAt = new Date(order.voidedAt)
  if (Number.isNaN(voidedAt.getTime())) return false
  const windowMs = GR_VOID_UNDO_WINDOW_DAYS * 24 * 60 * 60 * 1000
  return now.getTime() - voidedAt.getTime() <= windowMs
}

/** Invoice re-attached / soft-linked after void — block undo until cleared. */
export function orderHasInvoiceRelinkedAfterVoid(
  order = {},
  uploadedInvoices = [],
) {
  if (hasGoodsReceiptingInvoiceLinked(order)) return true
  return getLinkedInvoicesForReceiptingOrder(order, uploadedInvoices).length > 0
}

/**
 * Undo void within 7 days when still finance-safe and not re-linked.
 */
export function canUndoVoidGoodsReceiptingOrder(
  order = {},
  uploadedInvoices = [],
  now = new Date(),
) {
  if (!isVoidedGoodsReceiptingOrder(order)) return false
  if (!isWithinVoidUndoWindow(order, now)) return false
  if (isVoidBlockedByFinance(order, uploadedInvoices)) return false
  if (orderHasInvoiceRelinkedAfterVoid(order, uploadedInvoices)) return false
  return true
}

export function getUndoVoidGoodsReceiptingDisabledReason(
  order = {},
  uploadedInvoices = [],
  now = new Date(),
) {
  if (!isVoidedGoodsReceiptingOrder(order)) {
    return GR_VOID_COPY.undoBlockedNotVoided
  }
  if (!isWithinVoidUndoWindow(order, now)) {
    return GR_VOID_COPY.undoBlockedWindow
  }
  if (isVoidBlockedByFinance(order, uploadedInvoices)) {
    return GR_VOID_COPY.undoBlockedFinance
  }
  if (orderHasInvoiceRelinkedAfterVoid(order, uploadedInvoices)) {
    return GR_VOID_COPY.undoBlockedRelinked
  }
  return null
}
